import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Download, Loader2, RefreshCcw, Database, SlidersHorizontal, X } from 'lucide-react';

const STATUS_STYLES = {
  pending:   { label: 'Running',   bg: 'rgba(245,158,11,0.10)', color: '#B45309', border: 'rgba(245,158,11,0.25)' },
  completed: { label: 'Ready',     bg: 'rgba(14,107,93,0.10)',  color: '#0E6B5D', border: 'rgba(14,107,93,0.22)' },
  failed:    { label: 'Failed',    bg: 'rgba(153,27,27,0.10)',  color: '#991B1B', border: 'rgba(153,27,27,0.25)' },
};

const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const summarizeFilters = (f, brandMap, catMap) => {
  if (!f) return '';
  const parts = [];
  if (f.label) parts.push(f.label);
  if (f.date_from || f.date_to) {
    parts.push(`${f.date_from || '…'} → ${f.date_to || '…'}`);
  }
  if (f.brand_ids && f.brand_ids.length) {
    parts.push(`${f.brand_ids.length} brand${f.brand_ids.length > 1 ? 's' : ''}`);
  }
  if (f.category_ids && f.category_ids.length) {
    parts.push(`${f.category_ids.length} categor${f.category_ids.length > 1 ? 'ies' : 'y'}`);
  }
  return parts.join(' · ');
};

const EMPTY_FILTERS = { date_from: '', date_to: '', brand_ids: [], category_ids: [], label: '' };

export default function ExportsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/exports/dataset');
      setJobs(data.jobs || []);
    } catch (e) {
      toast.error('Failed to load exports');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Load brand + category options once; used for filter selects AND for
    // rendering filter summaries on past jobs.
    api.get('/admin/catalog/brands').then(r => setBrands(r.data?.brands || r.data || [])).catch(() => {});
    api.get('/admin/catalog/categories').then(r => setCategories(r.data?.categories || r.data || [])).catch(() => {});
  }, []);

  // Poll while any job is still pending
  useEffect(() => {
    if (!jobs.some(j => j.status === 'pending')) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [jobs]);

  const startExport = async () => {
    setStarting(true);
    try {
      const payload = {
        date_from: filters.date_from || null,
        date_to: filters.date_to || null,
        brand_ids: filters.brand_ids,
        category_ids: filters.category_ids,
        label: filters.label || null,
      };
      await api.post('/admin/exports/dataset', payload);
      toast.success('Export started — this may take a minute');
      setShowFilters(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start export');
    } finally { setStarting(false); }
  };

  const refreshJob = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/exports/dataset/${jobId}`);
      setJobs(prev => prev.map(j => j.id === jobId ? data : j));
    } catch (e) {
      toast.error('Failed to refresh job');
    }
  };

  const toggleId = (list, id) => list.includes(id) ? list.filter(x => x !== id) : [...list, id];

  const hasActiveFilters = filters.date_from || filters.date_to || filters.brand_ids.length || filters.category_ids.length || filters.label;

  return (
    <div className="max-w-4xl" data-testid="exports-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-ink flex items-center gap-2">
            <Database className="h-5 w-5" /> Dataset Exports
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Export every authenticated / counterfeit item as a JSONL file with brand, category,
            label, and image URLs. Intended for ML training. Item identifiers are anonymized.
            Files older than 30 days are auto-purged from storage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline" className="text-xs gap-1"
            onClick={() => setShowFilters(v => !v)}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="h-3 w-3" />
            Filters
            {hasActiveFilters && <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1.5">on</Badge>}
          </Button>
          <Button
            size="sm" className="text-xs"
            onClick={startExport} disabled={starting}
            data-testid="start-export-btn"
          >
            {starting ? <><Loader2 className="h-3 w-3 animate-spin mr-1.5" />Starting…</> : 'New export'}
          </Button>
        </div>
      </div>

      {showFilters && (
        <Card className="border-stone/50 mb-4" data-testid="filters-panel">
          <CardContent className="p-4 space-y-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Filters</span>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="text-[11px] text-muted-foreground hover:text-ink flex items-center gap-1"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  data-testid="clear-filters-btn"
                >
                  <X className="h-3 w-3" /> Clear
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">From (determination date)</label>
                <Input type="date" value={filters.date_from}
                  onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                  className="mt-1 h-8 text-xs" data-testid="filter-date-from" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</label>
                <Input type="date" value={filters.date_to}
                  onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                  className="mt-1 h-8 text-xs" data-testid="filter-date-to" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Label</label>
              <div className="mt-1 flex gap-2">
                {['', 'authentic', 'counterfeit'].map(lbl => (
                  <button key={lbl || 'both'} type="button"
                    onClick={() => setFilters(f => ({ ...f, label: lbl }))}
                    className={`px-3 py-1 rounded-full text-[11px] border ${filters.label === lbl ? 'bg-ink text-white border-ink' : 'bg-white border-stone text-muted-foreground'}`}
                    data-testid={`filter-label-${lbl || 'both'}`}
                  >
                    {lbl ? lbl : 'both'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Brands ({filters.brand_ids.length} selected)
              </label>
              <div className="mt-1 flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 border border-stone/40 rounded" data-testid="filter-brands">
                {brands.length === 0 ? (
                  <span className="text-[11px] text-muted-foreground">No brands loaded</span>
                ) : brands.map(b => (
                  <button key={b.id} type="button"
                    onClick={() => setFilters(f => ({ ...f, brand_ids: toggleId(f.brand_ids, b.id) }))}
                    className={`px-2 py-0.5 rounded text-[11px] border ${filters.brand_ids.includes(b.id) ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-stone/40 text-muted-foreground'}`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Categories ({filters.category_ids.length} selected)
              </label>
              <div className="mt-1 flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 border border-stone/40 rounded" data-testid="filter-categories">
                {categories.length === 0 ? (
                  <span className="text-[11px] text-muted-foreground">No categories loaded</span>
                ) : categories.map(c => (
                  <button key={c.id} type="button"
                    onClick={() => setFilters(f => ({ ...f, category_ids: toggleId(f.category_ids, c.id) }))}
                    className={`px-2 py-0.5 rounded text-[11px] border ${filters.category_ids.includes(c.id) ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-stone/40 text-muted-foreground'}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-stone/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-xs text-muted-foreground" data-testid="exports-empty">
              No exports yet. Click <strong>New export</strong> to generate the first one.
            </div>
          ) : (
            <div className="divide-y divide-stone/40">
              {jobs.map(job => {
                const style = STATUS_STYLES[job.status] || STATUS_STYLES.failed;
                const filterSummary = summarizeFilters(job.filters);
                const isPurged = !!job.purged_at;
                return (
                  <div key={job.id} className="flex items-center gap-4 p-4 text-xs" data-testid={`export-row-${job.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge
                          variant="outline"
                          style={{ backgroundColor: style.bg, color: style.color, borderColor: style.border }}
                          className="text-[10px] font-medium"
                          data-testid={`export-status-${job.id}`}
                        >
                          {style.label}
                        </Badge>
                        {isPurged && (
                          <Badge variant="outline" className="text-[10px] bg-stone/20 text-muted-foreground" data-testid={`export-purged-${job.id}`}>
                            Purged
                          </Badge>
                        )}
                        <span className="font-mono text-muted-foreground">{job.id.slice(0, 8)}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{fmtDate(job.created_at)}</span>
                      </div>
                      <div className="text-muted-foreground">
                        {job.item_count != null ? `${job.item_count} items` : '—'}
                        {' · '}{fmtBytes(job.size_bytes)}
                        {job.requested_by_email && <> · by <span className="font-mono">{job.requested_by_email}</span></>}
                        {filterSummary && (
                          <div className="mt-0.5 text-[11px] text-ink/70" data-testid={`export-filters-${job.id}`}>
                            Filters: {filterSummary}
                          </div>
                        )}
                        {job.error && (
                          <div className="mt-1 text-[#991B1B]" data-testid={`export-error-${job.id}`}>
                            Error: {job.error}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {job.status === 'completed' && job.download_url && !isPurged ? (
                        <>
                          <Button
                            asChild size="sm" variant="outline" className="text-[11px] h-7 gap-1"
                          >
                            <a href={job.download_url} target="_blank" rel="noreferrer"
                               data-testid={`export-download-${job.id}`}>
                              <Download className="h-3 w-3" /> Download
                            </a>
                          </Button>
                          <Button
                            size="sm" variant="ghost" className="text-[11px] h-7 gap-1"
                            onClick={() => refreshJob(job.id)}
                            data-testid={`export-refresh-${job.id}`}
                            title="Refresh signed URL"
                          >
                            <RefreshCcw className="h-3 w-3" />
                          </Button>
                        </>
                      ) : job.status === 'pending' ? (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" /> Building…
                        </div>
                      ) : isPurged ? (
                        <span className="text-[11px] text-muted-foreground">File auto-purged</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
